import type { AudioFrame, StreamFormat } from '@auxcast/protocol';
import { DEFAULTS, FrameDecodeError, PayloadType, decodeFrame } from '@auxcast/protocol';

import type { DecoderKind, OpusAdapter } from './decoder';
import { createOpusAdapter } from './decoder';
import { clampJitterMs, jitterSizing } from './jitter';
import { PeakMeter } from './meter';
import { OutputQueue } from './outputQueue';
import { LinearResampler, pcmFrameToPlanar } from './pcm';
import { RateMeter } from './rate';
import { SequenceTracker } from './sequence';
import type { WorkletCommand, WorkletStats } from './worklet/messages';
import { PLAYBACK_PROCESSOR } from './worklet/playbackProcessor';
import workletUrl from './worklet/playbackProcessor.ts?worker&url';

export interface PipelineStats {
  decoder: DecoderKind | 'pcm' | null;
  bitrateBps: number;
  bufferedMs: number;
  targetMs: number;
  underruns: number;
  discardedMs: number;
  lostFrames: number;
  concealedFrames: number;
  malformedFrames: number;
  framesPlayed: number;
  contextSampleRate: number;
  resampling: boolean;
  bufferState: 'filling' | 'playing';
}

export interface PipelineOptions {
  /** Called when the decoder is unusable and playback cannot continue. */
  onFatal?(message: string): void;
}

/** What the UI shows before any audio has been started. */
export const IDLE_STATS: PipelineStats = {
  decoder: null,
  bitrateBps: 0,
  bufferedMs: 0,
  targetMs: DEFAULTS.jitterBufferMs,
  underruns: 0,
  discardedMs: 0,
  lostFrames: 0,
  concealedFrames: 0,
  malformedFrames: 0,
  framesPlayed: 0,
  contextSampleRate: DEFAULTS.sampleRate,
  resampling: false,
  bufferState: 'filling',
};

/**
 * Everything between "bytes off the socket" and "sound out of the speakers".
 *
 * Lives on the main thread and owns the decoder; the jitter buffer and the actual
 * pull-model playback live in the AudioWorklet, which this feeds by transferring
 * decoded Float32Array buffers across.
 */
export class AudioPipeline {
  private readonly context: AudioContext;
  private readonly node: AudioWorkletNode;
  private readonly gain: GainNode;
  private readonly queue: OutputQueue;
  private readonly sequence = new SequenceTracker();
  private readonly rate = new RateMeter();
  private readonly meterL = new PeakMeter();
  private readonly meterR = new PeakMeter();
  private readonly options: PipelineOptions;

  private adapter: OpusAdapter | null = null;
  private adapterGeneration = 0;
  private adapterPending = false;
  private preferWasm = false;
  private format: StreamFormat | null = null;
  private resampler: LinearResampler | null = null;
  private targetMs: number = DEFAULTS.jitterBufferMs;
  private volume = 1;
  private muted = false;
  private closed = false;

  private malformed = 0;
  private framesPlayed = 0;
  private workletUnderruns = 0;
  private workletDiscarded = 0;
  private bufferedSamples = 0;
  private bufferState: 'filling' | 'playing' = 'filling';
  private lastMeterAt = 0;

  private constructor(context: AudioContext, node: AudioWorkletNode, options: PipelineOptions) {
    this.context = context;
    this.node = node;
    this.options = options;

    this.gain = context.createGain();
    this.gain.gain.value = 1;
    node.connect(this.gain);
    this.gain.connect(context.destination);

    node.port.onmessage = (event: MessageEvent<WorkletStats>) => {
      const stats = event.data;
      if (!stats || stats.type !== 'stats') return;
      this.bufferedSamples = stats.bufferedSamples;
      this.workletUnderruns = stats.underruns;
      this.workletDiscarded = stats.discardedSamples;
      this.bufferState = stats.state;
    };

    this.queue = new OutputQueue({
      emit: (planes) => this.emit(planes),
      fallbackChannels: DEFAULTS.channels,
      fallbackSamples: (DEFAULTS.sampleRate * DEFAULTS.frameMs) / 1000,
    });

    const sizing = jitterSizing(this.targetMs, context.sampleRate);
    this.post({
      type: 'format',
      // The graph is always stereo and the context rate never changes, so this is
      // the only allocation the audio thread ever does.
      channels: 2,
      capacitySamples: sizing.capacitySamples,
      targetSamples: sizing.targetSamples,
      capSamples: sizing.capSamples,
    });
  }

  /**
   * Must be called from a user gesture — browsers will not let an AudioContext
   * start otherwise, which is the whole reason the listener page opens on a
   * "Listen" button instead of auto-playing.
   */
  static async create(options: PipelineOptions = {}): Promise<AudioPipeline> {
    let context: AudioContext;
    try {
      context = new AudioContext({
        sampleRate: DEFAULTS.sampleRate,
        latencyHint: 'interactive',
      });
    } catch {
      // Some devices pin the hardware rate and reject the constraint outright.
      context = new AudioContext({ latencyHint: 'interactive' });
    }

    try {
      await context.audioWorklet.addModule(workletUrl);
    } catch (error) {
      await context.close().catch(() => undefined);
      throw new Error(
        `could not load the audio worklet: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    let node: AudioWorkletNode;
    try {
      node = new AudioWorkletNode(context, PLAYBACK_PROCESSOR, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
    } catch (error) {
      await context.close().catch(() => undefined);
      throw new Error(
        `could not create the playback node: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }

    // Rejects on a device the browser cannot open; `suspended` then stays true and
    // the UI offers the user a second gesture rather than playing nothing silently.
    await context.resume().catch(() => undefined);
    return new AudioPipeline(context, node, options);
  }

  get sampleRate(): number {
    return this.context.sampleRate;
  }

  get decoderKind(): DecoderKind | 'pcm' | null {
    return this.adapter?.kind ?? (this.format?.codec === 'pcm' ? 'pcm' : null);
  }

  /** iOS in particular can hand back a context that never left "suspended". */
  get suspended(): boolean {
    return this.context.state === 'suspended';
  }

  async resume(): Promise<void> {
    if (this.context.state === 'suspended') await this.context.resume().catch(() => undefined);
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, Number.isFinite(volume) ? volume : 1));
    this.applyGain();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyGain();
  }

  setTargetMs(ms: number): void {
    this.targetMs = ms;
    this.applySizing();
  }

  /** Announced format from `ready` / `stream-start`. Null means no sender. */
  setFormat(format: StreamFormat | null): void {
    if (this.closed) return;
    const changed =
      format?.codec !== this.format?.codec ||
      format?.channels !== this.format?.channels ||
      format?.sampleRate !== this.format?.sampleRate ||
      format?.frameMs !== this.format?.frameMs;
    this.format = format;
    if (!changed) return;

    this.adapterGeneration += 1;
    this.adapter?.close();
    this.adapter = null;
    this.adapterPending = false;
    this.queue.reset();
    this.sequence.reset();
    this.post({ type: 'reset' });

    if (!format) {
      this.resampler = null;
      return;
    }

    const frameSamples = Math.round((format.sampleRate * format.frameMs) / 1000);
    this.queue.setFallbackShape(format.channels, frameSamples);

    const ratio = this.context.sampleRate / format.sampleRate;
    this.resampler =
      Math.abs(ratio - 1) < 1e-9 ? null : new LinearResampler(format.channels, ratio);

    this.applySizing();
    if (format.codec === 'opus') void this.ensureAdapter();
  }

  /** Called when the stream stops so the next start does not play stale audio. */
  flush(): void {
    this.queue.clear();
    this.sequence.reset();
    this.resampler?.reset();
    this.adapter?.reset();
    this.post({ type: 'reset' });
  }

  /** One binary WebSocket message: 16-byte header plus payload. */
  pushFrame(buffer: ArrayBuffer): void {
    if (this.closed) return;

    const now = Date.now();
    this.rate.add(buffer.byteLength, now);

    let frame: AudioFrame;
    try {
      frame = decodeFrame(new Uint8Array(buffer));
    } catch (error) {
      this.malformed += 1;
      if (!(error instanceof FrameDecodeError)) throw error;
      return;
    }

    this.meterL.push(frame.peakL, now);
    this.meterR.push(frame.peakR, now);

    const decision = this.sequence.observe(frame.seq, frame.flags);
    if (decision.action === 'drop') return;
    if (decision.action === 'reset') {
      this.queue.clear();
      this.adapter?.reset();
      this.resampler?.reset();
      this.post({ type: 'reset' });
    }
    for (let i = 0; i < decision.conceal; i++) this.queue.reserveConcealment(i);

    if (frame.payloadType === PayloadType.PCM_F32LE) {
      const planes = pcmFrameToPlanar(frame.payload, frame.sampleCount);
      if (planes.length > 0) this.queue.enqueue(planes);
      return;
    }

    const adapter = this.adapter ?? this.ensureAdapterSync();
    if (!adapter) return;
    this.queue.reserve();
    adapter.decode(frame.payload);
  }

  /** Meter values for the UI, decayed to `atMs`. */
  sampleMeters(atMs: number): { left: number; right: number; holdL: number; holdR: number } {
    const elapsed = this.lastMeterAt === 0 ? 0 : atMs - this.lastMeterAt;
    this.lastMeterAt = atMs;
    const left = this.meterL.sample(atMs, elapsed);
    const right = this.meterR.sample(atMs, elapsed);
    return { left: left.level, right: right.level, holdL: left.hold, holdR: right.hold };
  }

  stats(): PipelineStats {
    const rate = this.context.sampleRate;
    return {
      decoder: this.decoderKind,
      bitrateBps: Math.round(this.rate.bitsPerSecond(Date.now())),
      bufferedMs: (this.bufferedSamples / rate) * 1000,
      targetMs: this.targetMs,
      underruns: this.workletUnderruns,
      discardedMs: (this.workletDiscarded / rate) * 1000,
      lostFrames: this.sequence.lostFrames,
      concealedFrames: this.sequence.concealedFrames,
      malformedFrames: this.malformed,
      framesPlayed: this.framesPlayed,
      contextSampleRate: rate,
      resampling: this.resampler !== null,
      bufferState: this.bufferState,
    };
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.adapterGeneration += 1;
    this.adapter?.close();
    this.adapter = null;
    this.node.port.onmessage = null;
    try {
      this.node.disconnect();
      this.gain.disconnect();
    } catch {
      /* already detached */
    }
    await this.context.close().catch(() => undefined);
  }

  private emit(planes: Float32Array[]): void {
    if (this.closed || planes.length === 0) return;
    const resampled = this.resampler ? this.resampler.process(planes) : planes;
    if ((resampled[0]?.length ?? 0) === 0) return;

    const transfer: ArrayBuffer[] = [];
    for (const plane of resampled) {
      const buffer = plane.buffer as ArrayBuffer;
      if (!transfer.includes(buffer)) transfer.push(buffer);
    }
    this.framesPlayed += 1;
    this.node.port.postMessage({ type: 'frames', planes: resampled } as WorkletCommand, transfer);
  }

  private post(command: WorkletCommand): void {
    if (this.closed) return;
    this.node.port.postMessage(command);
  }

  private applyGain(): void {
    const value = this.muted ? 0 : this.volume;
    const now = this.context.currentTime;
    // A ramp rather than a step: an instant gain change on a live signal clicks.
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setTargetAtTime(value, now, 0.015);
  }

  private applySizing(): void {
    this.targetMs = clampJitterMs(this.targetMs);
    const sizing = jitterSizing(this.targetMs, this.context.sampleRate);
    this.post({
      type: 'tune',
      targetSamples: sizing.targetSamples,
      capSamples: sizing.capSamples,
    });
  }

  private ensureAdapterSync(): OpusAdapter | null {
    if (!this.adapter && !this.adapterPending) void this.ensureAdapter();
    return this.adapter;
  }

  private async ensureAdapter(): Promise<void> {
    const format = this.format;
    if (!format || this.adapter || this.adapterPending || this.closed) return;

    this.adapterPending = true;
    const generation = this.adapterGeneration;
    try {
      const adapter = await createOpusAdapter(
        format,
        {
          onAudio: (planes) => {
            if (generation === this.adapterGeneration) this.queue.fulfil(planes);
          },
          onError: (error) => this.handleDecoderError(generation, error),
        },
        this.preferWasm,
      );
      if (generation !== this.adapterGeneration || this.closed) {
        adapter.close();
        return;
      }
      this.adapter = adapter;
    } catch (error) {
      if (generation === this.adapterGeneration) {
        this.options.onFatal?.(
          `no usable Opus decoder: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    } finally {
      if (generation === this.adapterGeneration) this.adapterPending = false;
    }
  }

  /**
   * A WebCodecs decoder that fails at runtime is not going to recover, so swap to
   * the WASM decoder once and stay there for the rest of the session.
   */
  private handleDecoderError(generation: number, error: unknown): void {
    if (generation !== this.adapterGeneration || this.closed) return;

    const failing = this.adapter;
    this.adapterGeneration += 1;
    this.adapter = null;
    this.adapterPending = false;
    this.queue.clear();
    this.sequence.reset();
    failing?.close();

    if (failing?.kind === 'webcodecs') {
      this.preferWasm = true;
      void this.ensureAdapter();
      return;
    }
    this.options.onFatal?.(
      `audio decoding failed: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
